import React, { useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { AlertTriangle } from 'lucide-react';
import { Button, Form, Modal } from 'react-bootstrap';

const CreditNoteItemListBootstrap = ({
    items = [],
    onItemsChange,
    roundTotal,
    onRoundTotalChange,
    currency = 'SEK',
    originalInvoiceNumber
}) => {
    const [showTextModal, setShowTextModal] = useState(false);
    const [newText, setNewText] = useState('');
    const [expandedItems, setExpandedItems] = useState({});

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('sv-SE', {
            style: 'currency',
            currency: currency,
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        }).format(amount);
    };

    const toggleItemExpanded = (itemId) => {
        setExpandedItems(prev => ({
            ...prev,
            [itemId]: !prev[itemId]
        }));
    };

    // Calculate totals (negative for credit notes)
    const calculateTotals = () => {
        const activeCreditItems = items.filter(
            item => item.type !== 'Text' && item.quantity > 0
        );

        const taxGroups = {};
        let subtotal = 0;

        activeCreditItems.forEach(item => {
            const quantity = parseFloat(item.quantity) || 0;
            const unitPrice = parseFloat(item.unitprice) || 0;
            const taxRate = parseFloat(item.tax_rate?.replace('%', '')) || 0;

            const lineTotal = quantity * unitPrice;

            if (!taxGroups[taxRate]) {
                taxGroups[taxRate] = { subtotal: 0, tax: 0 };
            }

            taxGroups[taxRate].subtotal += lineTotal;
            taxGroups[taxRate].tax += lineTotal * (taxRate / 100);

            subtotal += lineTotal;
        });

        const totalTax = Object.values(taxGroups).reduce((sum, group) => sum + group.tax, 0);
        const grandTotal = subtotal + totalTax;

        let roundingAmount = 0;
        let finalTotal = grandTotal;

        if (roundTotal && grandTotal > 0) {
            finalTotal = Math.round(grandTotal);
            roundingAmount = finalTotal - grandTotal;
        }

        return {
            taxGroups,
            subtotal: -subtotal, // Negative for credit notes
            totalTax: -totalTax, // Negative for credit notes
            grandTotal: -grandTotal, // Negative for credit notes
            roundingAmount: -roundingAmount, // Negative for credit notes
            finalTotal: -finalTotal // Negative for credit notes
        };
    };

    const calculations = calculateTotals();

    const handleQuantityChange = (itemId, quantity) => {
        const updatedItems = items.map(item => {
            if (item.id !== itemId) return item;

            const maxQuantity = item.original_quantity || 0;
            // Ensure quantity is not negative and doesn't exceed original
            const newQuantity = Math.min(
                Math.max(0, parseInt(quantity) || 0),
                maxQuantity
            );

            return { ...item, quantity: newQuantity };
        });

        onItemsChange(updatedItems);
    };

    const handleAddTextItem = () => {
        if (!newText.trim()) return;

        const textItem = {
            id: uuidv4(),
            type: 'Text',
            description: newText,
            quantity: 0,
            unitprice: 0,
            tax_rate: '0%'
        };

        onItemsChange([...items, textItem]);
        setNewText('');
        setShowTextModal(false);
    };

    const handleDeleteItem = (itemId) => {
        const updatedItems = items.filter(item => item.id !== itemId);
        onItemsChange(updatedItems);
    };

    // Process regular items vs text items
    const regularItems = items.filter(item => item.type !== 'Text');
    const textItems = items.filter(item => item.type === 'Text');

    return (
        <div className="mb-4">
            <div className="d-flex justify-content-between align-items-center mb-3">
                <h5>Artiklar att kreditera</h5>
                <div className="small text-muted">
                    Totalt: {regularItems.filter(item => item.quantity > 0).length} artikel{regularItems.filter(item => item.quantity > 0).length !== 1 ? 'ar' : ''}
                </div>
            </div>

            {/* Original invoice reference card 
            {originalInvoiceNumber && (
                <div className="card mb-4">
                    <div className="card-header d-flex justify-content-between align-items-center">
                        <h6 className="mb-0">Krediterar faktura #{originalInvoiceNumber}</h6>
                        <span className="badge bg-info">Återbetalning</span>
                    </div>
                </div>
            )}
            */}

            {regularItems.length === 0 ? (
                <div className="text-center py-5 bg-light rounded border">
                    <p className="text-muted mb-3">Inga artiklar tillgängliga för kreditering</p>
                </div>
            ) : (
                <>
                    {regularItems.map((item) => (
                        <div key={item.id} className="card mb-3 border border-primary border-dashed">
                            <div className="card-body p-3">
                                <div className="d-flex justify-content-between align-items-center">
                                    <div className="d-flex align-items-center">
                                        <button
                                            type="button"
                                            className="btn btn-sm me-2"
                                            onClick={() => toggleItemExpanded(item.id)}
                                            aria-label={expandedItems[item.id] ? 'Minimera' : 'Expandera'}
                                        >
                                            <i className={`bi bi-chevron-${expandedItems[item.id] ? 'up' : 'down'}`}></i>
                                        </button>
                                        <div>
                                            <strong>{item.productcode || 'Artikel'}</strong>
                                            {!expandedItems[item.id] && (
                                                <span className="text-muted ms-2 small">{item.description}</span>
                                            )}
                                        </div>
                                    </div>
                                    <div className="d-flex align-items-center">
                                        <span className="me-3 text-end text-danger">
                                            <div>{formatCurrency(-1 * item.quantity * item.unitprice)}</div>
                                            <small className="text-muted">
                                                {item.quantity} × {formatCurrency(item.unitprice)}
                                            </small>
                                        </span>
                                    </div>
                                </div>

                                {expandedItems[item.id] && (
                                    <div className="mt-3 animate__animated animate__fadeIn">
                                        <div className="mb-3">
                                            <label className="form-label">Beskrivning</label>
                                            <p className="mb-0">{item.description}</p>
                                        </div>

                                        <div className="row mb-3">
                                            <div className="col-md-4">
                                                <label className="form-label">Ursprungligt antal</label>
                                                <p className="mb-0">{item.original_quantity}</p>
                                            </div>

                                            <div className="col-md-4">
                                                <label className="form-label">Antal att kreditera</label>
                                                <input
                                                    type="number"
                                                    className="form-control"
                                                    min="0"
                                                    max={item.original_quantity}
                                                    value={item.quantity}
                                                    onChange={(e) => handleQuantityChange(item.id, e.target.value)}
                                                />
                                                {item.quantity > item.original_quantity && (
                                                    <div className="text-danger small mt-1">
                                                        <AlertTriangle size={12} className="me-1" />
                                                        Kan inte överskrida ursprungligt antal
                                                    </div>
                                                )}
                                            </div>

                                            <div className="col-md-4">
                                                <label className="form-label">À-pris</label>
                                                <p className="mb-0">{formatCurrency(item.unitprice)}</p>
                                            </div>
                                        </div>

                                        <div className="d-flex justify-content-between align-items-center pt-2 border-top">
                                            <span className="text-muted">
                                                Delsumma: {formatCurrency(item.quantity * item.unitprice)}
                                            </span>
                                            <span className="fw-medium">
                                                Moms ({item.tax_rate}): {formatCurrency(
                                                    item.quantity * item.unitprice *
                                                    (parseFloat(item.tax_rate?.replace('%', '')) / 100)
                                                )}
                                            </span>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    ))}
                </>
            )}

            {/* Text items section */}
            {textItems.length > 0 && (
                <div className="mb-4 p-3 rounded border-start border-4 border-primary">
                    <div className="d-flex align-items-center mb-3">
                        <h6 className="mb-0 me-2">Krediteringsnoteringar</h6>
                        <span className="badge bg-light text-secondary">{textItems.length}</span>
                    </div>

                    <div className="px-1">
                        {textItems.map((item) => (
                            <div key={item.id} className="d-flex justify-content-between py-2 border-bottom">
                                <div className="fst-italic text-secondary">{item.description}</div>
                                <button
                                    type="button"
                                    className="btn btn-sm text-danger p-0 px-2"
                                    onClick={() => handleDeleteItem(item.id)}
                                >
                                    <i className="bi bi-trash"></i>
                                </button>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            <div className="mt-4 d-flex justify-content-center">
                <button
                    className="w-50 mt-4 mb-4 btn btn-outline-primary border-dashed"
                    onClick={() => setShowTextModal(true)}
                >
                    <i className="bi bi-plus"></i> Lägg till anteckning
                </button>
            </div>

            {/* Credit Note Summary section - matches InvoiceItemListBootstrap structure */}
            {regularItems.filter(item => item.quantity > 0).length > 0 && (
                <>
                    <style>{`
                        .credit-note-summary {
                            margin-top: 2rem;
                            padding-top: 1rem;
                            border-top: 1px solid #e5e5e5;
                        }
                        
                        .credit-summary-card {
                            background: #fff5f5;
                            padding: 1.5rem;
                            border-radius: 8px;
                            border: 1px solid #fecaca;
                            box-shadow: 0 2px 4px rgba(0,0,0,0.05);
                        }
                        
                        .credit-summary-row {
                            display: flex;
                            justify-content: space-between;
                            align-items: center;
                            margin-bottom: 0.75rem;
                            padding: 0.25rem 0;
                        }
                        
                        .credit-summary-row.total {
                            margin-top: 1rem;
                            padding-top: 1rem;
                            border-top: 2px solid #fecaca;
                            font-weight: bold;
                            font-size: 1.1em;
                        }
                        
                        .credit-value {
                            color: #dc2626;
                            font-weight: 600;
                        }
                        
                        .credit-total-value {
                            color: #dc2626;
                            font-weight: bold;
                            font-size: 1.2em;
                        }
                    `}</style>

                    <div className="credit-note-summary mt-4">
                        <div className="row justify-content-end">
                            <div className="col-md-6">
                                <div className="credit-summary-card">
                                    <div className="credit-summary-row">
                                        <span className="label">Delsumma (exkl. moms):</span>
                                        <span className="credit-value">{formatCurrency(calculations.subtotal)}</span>
                                    </div>

                                    {Object.entries(calculations.taxGroups).map(([rate, data]) => (
                                        <div className="credit-summary-row" key={rate}>
                                            <span className="label">Moms ({rate}%):</span>
                                            <span className="credit-value">{formatCurrency(data.tax)}</span>
                                        </div>
                                    ))}

                                    {roundTotal && Math.abs(calculations.roundingAmount) > 0.001 && (
                                        <div className="credit-summary-row">
                                            <span className="label">Avrundning:</span>
                                            <span className="credit-value">{formatCurrency(calculations.roundingAmount)}</span>
                                        </div>
                                    )}

                                    <div className="credit-summary-row total">
                                        <span className="label">Kreditbelopp (inkl. moms):</span>
                                        <span className="credit-total-value">{formatCurrency(calculations.finalTotal)}</span>
                                    </div>

                                    <div className="credit-summary-row mt-3">
                                        <Form.Check
                                            type="switch"
                                            id="rounding-switch"
                                            label="Avrundning"
                                            checked={roundTotal}
                                            onChange={(e) => onRoundTotalChange(e.target.checked)}
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </>
            )}

            {/* Text item modal */}
            <div className="modal fade" id="textModal" tabIndex="-1" aria-hidden="true">
                <div className="modal-dialog">
                    <div className="modal-content">
                        <div className="modal-header">
                            <h5 className="modal-title">Lägg till textrad</h5>
                            <button type="button" className="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
                        </div>
                        <div className="modal-body">
                            <div className="mb-3">
                                <label htmlFor="textContent" className="form-label">Text</label>
                                <textarea
                                    className="form-control"
                                    id="textContent"
                                    rows="3"
                                    value={newText}
                                    onChange={(e) => setNewText(e.target.value)}
                                    placeholder="T.ex. 'Kreditering på grund av...'"
                                ></textarea>
                            </div>
                        </div>
                        <div className="modal-footer">
                            <button type="button" className="btn btn-secondary" data-bs-dismiss="modal">Avbryt</button>
                            <button
                                type="button"
                                className="btn btn-primary"
                                onClick={handleAddTextItem}
                                disabled={!newText.trim()}
                            >
                                Lägg till
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* Text item modal */}
            <Modal show={showTextModal} onHide={() => setShowTextModal(false)}>
                <Modal.Header closeButton>
                    <Modal.Title>Lägg till textrad</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form.Group>
                        <Form.Label>Text</Form.Label>
                        <Form.Control
                            as="textarea"
                            rows={3}
                            value={newText}
                            onChange={(e) => setNewText(e.target.value)}
                            placeholder="T.ex. 'Kreditering på grund av...'"
                        />
                    </Form.Group>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowTextModal(false)}>
                        Avbryt
                    </Button>
                    <Button
                        variant="primary"
                        onClick={handleAddTextItem}
                        disabled={!newText.trim()}
                    >
                        Lägg till
                    </Button>
                </Modal.Footer>
            </Modal>
        </div >
    );
};

export default CreditNoteItemListBootstrap;
